import React, { useEffect, useMemo, useState } from "react";
import ProviderListingCard from "../ServiceProvider/ProviderListingCard";
import "./ProviderListWithPagination.css";

const ProviderListWithPagination = ({
  items = [],
  id,
  eventDetails,
  cancallperist,
  tentHouseFilterApplied,
  selectedTentHouseServiceIds = [],
  serviceitemsadded,
  cancallcatering,
  searchText,
  quantity,
  by = 'eventservices'
}) => {
  
  const [page, setPage] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [tempSelectedItems, setTempSelectedItems] = useState([]);
  const pageSize = 5;

  // Debug log
  useEffect(() => {
    console.log("Selected Items updated:", selectedItems);
  }, [selectedItems]);

  // --- Extract all unique included item names for id === 9 ---
  const allIncludedItems = useMemo(() => {
    if (!(id === 9 && cancallcatering)) return [];
    
    const itemSet = new Set();
    items.forEach(provider => {
      provider.items?.forEach(item => {
        item.includedItems?.forEach(includedItem => {
          itemSet.add(includedItem.item_name);
        });
      });
    });
    
    return Array.from(itemSet).sort();
  }, [id, cancallcatering, items]);

  // --- Filter items based on selected item names ---
  const filteredItems = useMemo(() => {
    if (!(id === 9 && cancallcatering)) return items;
    
    if (selectedItems.length === 0) return items;
    
    const filtered = items.filter(provider => {
      return provider.items?.some(item => {
        return item.includedItems?.some(includedItem => 
          selectedItems.includes(includedItem.item_name)
        );
      });
    });
    
    console.log("Filtered items count:", filtered.length, "with filters:", selectedItems);
    return filtered;
  }, [id, cancallcatering, items, selectedItems]);

  // --- id === 9: build a flat, sorted service list across filtered providers ---
  const flatServices = useMemo(() => {
    if (!(id === 9 && cancallcatering)) return [];

    const selectedIds = (searchText ?? []).map((s) => s.itemId);
    
    const services = filteredItems
      .flatMap((provider) =>
        (provider.items ?? []).map((service) => {
          const matches =
            service.includedItems?.filter((i) =>
              selectedIds.includes(i.itemId)
            ).length || 0;

          return { provider, service, matches };
        })
      )
      .sort((a, b) => b.matches - a.matches);
    
    console.log("Flat services count:", services.length);
    return services;
  }, [id, cancallcatering, filteredItems, searchText]);

  // Remove item handler
  const handleRemoveItem = (itemNameToRemove) => {
    console.log("Removing item:", itemNameToRemove);
    setSelectedItems(prev => {
      const newItems = prev.filter(item => item !== itemNameToRemove);
      console.log("New selected items after removal:", newItems);
      return newItems;
    });
    setPage(0);
  };

  // Open filter dialog
  const openFilterDialog = () => {
    setTempSelectedItems([...selectedItems]);
    setShowFilterDialog(true);
  };

  // Close filter dialog
  const closeFilterDialog = () => {
    setShowFilterDialog(false);
  };

  // Apply filters from dialog
  const applyFilters = () => {
    setSelectedItems([...tempSelectedItems]);
    setPage(0);
    closeFilterDialog();
  };

  // Clear all selections in dialog
  const clearDialogFilters = () => {
    setTempSelectedItems([]);
  };

  // Toggle item selection in dialog
  const toggleDialogItemSelection = (itemName) => {
    setTempSelectedItems(prev => {
      if (prev.includes(itemName)) {
        return prev.filter(item => item !== itemName);
      } else {
        return [...prev, itemName];
      }
    });
  };

  // Clear all selections
  const clearFilters = () => {
    console.log("Clearing all filters");
    setSelectedItems([]);
    setPage(0);
  };

  // Decide what we're paginating (providers for 7/8, services for 9)
  const totalPages =
    id === 9 && cancallcatering
      ? Math.ceil(flatServices.length / pageSize)
      : Math.ceil(items.length / pageSize);

  // Clamp page on input changes
  useEffect(() => {
    setPage(0);
  }, [id, items.length, flatServices.length]);

  const handlePrev = () => setPage((p) => Math.max(p - 1, 0));
  const handleNext = () => setPage((p) => Math.min(p + 1, Math.max(totalPages - 1, 0)));

  // Slices
  const providerSlice =
    id !== 9
      ? items.slice(page * pageSize, (page + 1) * pageSize)
      : [];

  const serviceSlice =
    id === 9 && cancallcatering
      ? flatServices.slice(page * pageSize, (page + 1) * pageSize)
      : [];

  console.log("Render - selectedItems:", selectedItems, "serviceSlice:", serviceSlice.length);

  return (
    <div>
      {/* Filter Dialog Modal */}
      {showFilterDialog && (
        <div className="filter-dialog-overlay">
          <div className="filter-dialog">
            <div className="filter-dialog-header">
              <h3 className="filter-dialog-title">Filter by Included Items</h3>
              <button 
                className="filter-dialog-close"
                onClick={closeFilterDialog}
              >
                ×
              </button>
            </div>
            
            <div className="filter-dialog-body">
              <div className="filter-selected-count">
                Selected: {tempSelectedItems.length} item(s)
              </div>
              
              <div className="filter-items-container">
                {allIncludedItems.map(itemName => (
                  <div 
                    key={itemName}
                    className={`filter-item ${tempSelectedItems.includes(itemName) ? 'selected' : ''}`}
                    onClick={() => toggleDialogItemSelection(itemName)}
                  >
                    <div className="filter-item-checkbox">
                      {tempSelectedItems.includes(itemName) && '✓'}
                    </div>
                    <span className="filter-item-name">{itemName}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="filter-dialog-footer">
              <button 
                className="filter-dialog-btn secondary"
                onClick={clearDialogFilters}
              >
                Clear All
              </button>
              <div className="filter-dialog-actions">
                <button 
                  className="filter-dialog-btn"
                  onClick={closeFilterDialog}
                >
                  Cancel
                </button>
                <button 
                  className="filter-dialog-btn primary"
                  onClick={applyFilters}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ID = 9 Filter section - now with modal trigger */}
      {/* {id === 9 && cancallcatering && allIncludedItems.length > 0 && (
        <div className="filter-section">
          <div className="filter-header">
            <div className="filter-info">
              <h4 className="filter-title">Filter Catering Items</h4>
              {selectedItems.length > 0 && (
                <span className="filter-active-badge">
                  {selectedItems.length} filter(s) active
                </span>
              )}
            </div>
            
            <div className="filter-actions">
              {selectedItems.length > 0 && (
                <button 
                  className="filter-clear-btn"
                  onClick={clearFilters}
                >
                  Clear All
                </button>
              )}
              <button 
                className="filter-open-btn"
                onClick={openFilterDialog}
              >
                <i className="pi pi-filter" style={{ marginRight: '8px' }}></i>
                {selectedItems.length > 0 ? 'Edit Filters' : 'Filter Items'}
              </button>
            </div>
          </div>
          
          {selectedItems.length > 0 && (
            <div className="active-filters">
              <div className="active-filters-label">Active filters:</div>
              <div className="selected-items-list">
                {selectedItems.map(itemName => (
                  <span key={itemName} className="selected-item-tag">
                    {itemName}
                    <button 
                      className="remove-item-btn"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent any parent event handlers
                        handleRemoveItem(itemName);
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {selectedItems.length > 0 && (
            <div className="filter-results-info">
              Showing {filteredItems.length} provider(s) matching your filters
            </div>
          )}
        </div>
      )} */}

      {/* ID = 7 (Priest services) */}
      {id === 7 && cancallperist && (
        <>
          {providerSlice.map((item) => {
            const fullName = [item.firstName, item.lastName].filter(Boolean).join(" ");
            return (
              <ProviderListingCard
                serviceId="7"
                providerid={item.id}
                event={eventDetails}
                packages={item.packages}
                key={`7-${item.id}`}
                latitude={item.latitude}
                longitude={item.longitude}
                name={fullName}
                address={item.formattedAddress}
                distance={item.distanceInMeter}
                mobile={item.mobile}
                email={item.email}
                price={item.price}
                discount={item.discountPrice}
                validUpto={item.discountValidUpto}
              />
            );
          })}
        </>
      )}

      {/* ID = 8 (Tent house services) */}
      {id === 8 &&
        tentHouseFilterApplied &&
        selectedTentHouseServiceIds.length > 0 && (
          <>
            {providerSlice.map((item) => (
              <ProviderListingCard
                serviceId="8"
                quantity={serviceitemsadded}
                event={eventDetails}
                services={item.services}
                providerid={item.providerId}
                key={`8-${item.id}`}
                latitude={item.latitude}
                longitude={item.longitude}
                name={item.name}
                address={item.formattedAddress}
                distance={item.distanceInMeter}
                mobile={item.mobile}
                email={item.emailId}
                price={item.price}
                discount={item.discountPrice}
              />
            ))}
          </>
        )}

      {/* ID = 9 (Catering) — paginate services across ALL providers */}
      {id === 9 && cancallcatering && (
        <>
          {serviceSlice.length > 0 ? (
            serviceSlice.map(({ provider, service }, idx) => (
              <label
                key={`${provider.id}-${service.id ?? idx}`}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
              >
                <ProviderListingCard
                  serviceId="9"
                  services={service}
                  quantity={quantity}
                  providerid={provider.id}
                  event={eventDetails}
                  latitude={provider.latitude}
                  longitude={provider.longitude}
                  name={provider.name}
                  address={provider.formattedAddress}
                  distance={provider.distanceInMeter}
                  mobile={provider.mobile}
                  email={provider.email}
                  price={service.price ?? provider.price}
                  discount={service.discountPrice ?? provider.discountPrice}
                />
              </label>
            ))
          ) : (
            <div className="no-results-message">
              No catering providers match the selected filters.
              {selectedItems.length > 0 && (
                <div className="no-results-actions">
                  <button 
                    className="clear-filters-btn"
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          {page > 0 && (
            <button className="pagebuttonarrow" onClick={handlePrev}>
               <i style={{fontSize:"14px"}} className="pi pi-chevron-left"></i> Prev
            </button>
          )}

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setPage(i)}
              className={`pagebuttons ${page === i ? "active" : ""}`}
            >
              {i + 1}
            </button>
          ))}

          {page < totalPages - 1 && (
            <button className="pagebuttonarrow" onClick={handleNext}>
              Next <i style={{fontSize:"14px",color:"black"}} className="pi pi-chevron-right"></i>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProviderListWithPagination;